import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import PackageCard from "../components/PackageCard";
import { SlidersHorizontal, Search } from "lucide-react";
import Seo from "../components/Seo";

const DESTS = ["All", "Dubai", "Singapore", "Thailand", "Bali", "Vietnam", "Maldives", "Mauritius", "Europe", "Australia", "Andaman", "Kashmir", "Goa", "Kerala", "Himachal", "Uttarakhand", "Bhutan", "Nepal"];

export default function Packages() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("destination") || "All";
  const [items, setItems] = useState([]);
  const [dest, setDest] = useState(initial);
  const [cat, setCat] = useState("all");
  const [priceMax, setPriceMax] = useState(200000);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/packages").then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (dest !== "All") setParams({ destination: dest }); else setParams({});
  }, [dest]);

  const filtered = useMemo(() => items.filter(p => (
    (dest === "All" || p.destination === dest) &&
    (cat === "all" || p.category === cat) &&
    p.price <= priceMax &&
    (!q || p.title.toLowerCase().includes(q.toLowerCase()) || p.destination.toLowerCase().includes(q.toLowerCase()))
  )), [items, dest, cat, priceMax, q]);

  return (
    <div className="pt-24 lg:pt-28">
      <Seo title="Holiday Packages" path="/packages" description="Browse 100+ curated holiday packages for Dubai, Singapore, Thailand, Bali, Maldives, Kashmir, Kerala, Europe and more. Best prices per person, fully customizable." />
      <section className="container-p">
        <span className="section-label">Holiday Packages</span>
        <h1 className="h1 mt-3">Find your perfect getaway</h1>
        <p className="muted mt-4 max-w-2xl">17+ destinations · 100+ packages · Curated by travel experts. Prices per person on twin sharing.</p>
      </section>

      <section className="container-p mt-10">
        <div className="card-glass p-4 md:p-5 flex flex-wrap gap-3 items-center" data-testid="packages-filter-bar">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-slate-50 rounded-xl px-4 py-2.5">
            <Search className="w-4 h-4 text-slate-400" />
            <input placeholder="Search packages…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-transparent flex-1 outline-none text-sm" data-testid="packages-search" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="bg-slate-50 rounded-xl px-4 py-2.5 text-sm" data-testid="packages-category">
            <option value="all">All Trips</option>
            <option value="domestic">Domestic</option>
            <option value="international">International</option>
          </select>
          <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-2.5 min-w-[220px]">
            <SlidersHorizontal className="w-4 h-4 text-slate-400" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Max ₹{priceMax.toLocaleString('en-IN')}</div>
              <input type="range" min="10000" max="200000" step="5000" value={priceMax} onChange={(e) => setPriceMax(+e.target.value)} className="w-full accent-[#FF7D00]" data-testid="packages-price-range" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="packages-dest-chips">
          {DESTS.map(d => (
            <button key={d} onClick={() => setDest(d)} className={`px-4 py-2 rounded-full text-sm font-medium transition ${dest === d ? "bg-[#0A2540] text-white" : "bg-slate-100 text-[#0A2540] hover:bg-slate-200"}`} data-testid={`chip-${d.toLowerCase()}`}>{d}</button>
          ))}
        </div>
      </section>

      <section className="container-p mt-10 mb-20">
        <div className="mb-6 muted text-sm">{loading ? "Loading…" : `Showing ${filtered.length} packages`}</div>
        {filtered.length === 0 && !loading ? (
          <div className="text-center py-20 muted">No packages match your filters. Try widening the price range or a different destination.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map(p => <PackageCard key={p.id} p={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
